import { useCallback, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { Check, ChevronDown } from "lucide-react";
import * as stylex from "@stylexjs/stylex";
import { recipes } from "@/design/recipes";
import "@/design/tokens.stylex";
import { colors, layout, radii, space, text } from "@/design/tokens.stylex";
import { focusBeside } from "./focusBeside";
import Popover from "./Popover";

// A compact single- or multi-choice picker: a chip-like trigger and a listbox
// with type-ahead in a Popover. Native <select> stays for long plain forms.
const styles = {
  trigger: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x166cprz",
    k7Eaqz: "xeuugli",
    kZKoxP: "x5esxvw",
    kg3NbH: "xtft4pz",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xzbo3x7 xist2jy",
    kaIpWk: "x16l1o21",
    kWkggS: "x1h1f4i9",
    kMwMTN: "xgwkh9f",
    kGuDYH: "xtu3avg",
    k9WMMc: "xdpxx8g",
    kSiTet: "x1hc1fzr x1t5pwa4",
    $$css: true
  },
  full: {
    kzqmXN: "xh8yej3",
    $$css: true
  },
  sm: {
    kZKoxP: "x1f38ord",
    kg3NbH: "x1wa2ap4",
    kGuDYH: "x7x6yu3",
    $$css: true
  },
  highlight: {
    kVAM5u: "x9r1u3d x13618eq",
    kWkggS: "x1i5inb7",
    k63SB2: "xk50ysn",
    $$css: true
  },
  quiet: {
    kVAM5u: "x9r1u3d x13618eq",
    kWkggS: "xjbqb8w x1ki36sz",
    $$css: true
  },
  chevron: {
    k1xSpc: "x3nfvp2",
    kmuXW: "x2lah0s",
    kMwMTN: "x1i6qc9y",
    $$css: true
  },
  menu: {
    kZeWKH: "xish69e",
    $$css: true
  },
  option: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x166cprz",
    kzqmXN: "xh8yej3",
    kAzted: "x21xpn4",
    kg3NbH: "x1wa2ap4",
    kaIpWk: "x1wpy2cq",
    kWkggS: "xjbqb8w x1fns82p x15twbwe",
    kMwMTN: "x1e3avs9",
    kGuDYH: "xtu3avg",
    k9WMMc: "xdpxx8g",
    kI3sdo: "x1a2a7pz",
    $$css: true
  },
  selected: {
    kMwMTN: "xgwkh9f",
    k63SB2: "xk50ysn",
    $$css: true
  },
  optionLabel: {
    kUk6DE: "x98rzlu",
    k7Eaqz: "xeuugli",
    $$css: true
  }
};
export interface MenuSelectOption<T extends string = string> {
  label: string;
  value: T;
}
export default function MenuSelect<T extends string>({
  value,
  selectedValues,
  selectionLabel,
  options,
  onValueChange,
  ariaLabel,
  "aria-describedby": ariaDescribedBy,
  disabled = false,
  tone = "plain",
  size = "md",
  fullWidth = false,
  xstyle,
  align = "left"
}: {
  value: T;
  selectedValues?: readonly T[];
  selectionLabel?: string;
  options: readonly MenuSelectOption<T>[];
  onValueChange: (value: T) => void;
  ariaLabel: string;
  /** Linked by a Field to its hint or error. */
  "aria-describedby"?: string;
  disabled?: boolean;
  tone?: "plain" | "highlight" | "quiet";
  size?: "sm" | "md";
  fullWidth?: boolean;
  xstyle?: stylex.StyleXStyles;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const typed = useRef({
    text: "",
    at: 0
  });
  const isSelected = (option: T) => selectedValues ? selectedValues.includes(option) : option === value;
  const selected = options.find(option => isSelected(option.value)) ?? options[0];
  const showing = open && !disabled;
  const close = useCallback((reason?: "outside" | "escape") => {
    setOpen(false);
    if (reason !== "outside") triggerRef.current?.focus();
  }, []);
  useLayoutEffect(() => {
    if (!showing) return;
    (listboxRef.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]') ?? listboxRef.current?.querySelector<HTMLButtonElement>('[role="option"]'))?.focus();
    typed.current = {
      text: "",
      at: 0
    };
  }, [showing]);
  function menuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const buttons = Array.from(listboxRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? []);
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
      focusBeside(triggerRef.current, event.shiftKey);
      return;
    }
    let next: number | undefined;
    if (event.key === "ArrowDown") next = (current + 1) % buttons.length;
    if (event.key === "ArrowUp") next = (current - 1 + buttons.length) % buttons.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = buttons.length - 1;
    if (event.key.length === 1 && event.key !== " " && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const now = Date.now();
      typed.current = {
        text: (now - typed.current.at < 500 ? typed.current.text : "") + event.key.toLocaleLowerCase(),
        at: now
      };
      next = buttons.findIndex(button => button.textContent?.toLocaleLowerCase().startsWith(typed.current.text));
    }
    if (next !== undefined) {
      event.preventDefault();
      buttons[next]?.focus();
    }
  }
  return <>
      <button ref={triggerRef} type="button" disabled={disabled} aria-label={ariaLabel} aria-describedby={ariaDescribedBy} aria-haspopup="listbox" aria-expanded={showing} aria-controls={showing ? listboxId : undefined} onClick={() => setOpen(current => !current)} onKeyDown={event => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        setOpen(true);
      }
    }} {...stylex.props(recipes.bareButton, styles.trigger, recipes.focusRing, recipes.transition, fullWidth && styles.full, size === "sm" && styles.sm, tone !== "plain" && styles[tone], xstyle)}>
        <span {...stylex.props(recipes.truncate)}>
          {selectionLabel ?? selected?.label ?? value}
        </span>
        <span aria-hidden className="x3nfvp2 x2lah0s x1i6qc9y">
          <ChevronDown size={14} />
        </span>
      </button>
      <Popover open={showing} anchorRef={triggerRef} onClose={close} align={align === "right" ? "end" : "start"} matchWidth maxHeight={300} xstyle={styles.menu}>
        <div ref={listboxRef} id={listboxId} role="listbox" aria-multiselectable={selectedValues ? true : undefined} aria-label={ariaLabel} onKeyDown={menuKeyDown}>
          {options.map(option => <button key={option.value} type="button" role="option" aria-selected={isSelected(option.value)} tabIndex={-1} onClick={() => {
          onValueChange(option.value);
          close();
        }} {...stylex.props(recipes.bareButton, styles.option, isSelected(option.value) && styles.selected)}>
              <span className="x98rzlu xeuugli">{option.label}</span>
              {isSelected(option.value) && <Check aria-hidden size={13} />}
            </button>)}
        </div>
      </Popover>
    </>;
}